import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { Alert, Button, ButtonGroup, Form } from 'react-bootstrap';

import JSZip from 'jszip';

import Icon from '@/components/Icon';
import Modal from '@/components/Modal';
import { useImageUpload } from '@/components/Editor/hooks/useImageUpload';

import {
  AnswerImportMode,
  extractAnswerImagePlaceholders,
  getImportedAnswerStats,
  matchAnswerImageFiles,
  mergeImportedAnswer,
  normalizeImportedAnswer,
  replaceAnswerImagePlaceholders,
} from './importUtils';
import { GEO_AI_ANSWER_TEMPLATE } from './utils';

const MAX_IMPORT_FILE_SIZE = 20 * 1024 * 1024;
const MAX_MARKDOWN_UNCOMPRESSED_SIZE = 2 * 1024 * 1024;
const MAX_ARCHIVE_IMAGE_COUNT = 30;
const MAX_ARCHIVE_TOTAL_IMAGE_SIZE = 25 * 1024 * 1024;
const ALLOWED_FILE_EXTENSIONS = ['.md', '.markdown', '.txt', '.json', '.zip'];
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
const IMAGE_MIME_TYPES: Record<string, string> = {
  '.gif': 'image/gif',
  '.jpeg': 'image/jpeg',
  '.jpg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
};

interface Props {
  content: string;
  onImport: (content: string) => void;
}

interface ArchiveImageFile extends File {
  archivePath?: string;
}

type NoticeVariant = 'success' | 'warning' | 'info';

interface ImportNotice {
  text: string;
  variant: NoticeVariant;
}

const getFileExtension = (fileName: string) => {
  const cleanName = fileName.split(/[?#]/, 1)[0];
  const dotIndex = cleanName.lastIndexOf('.');
  return dotIndex >= 0 ? cleanName.slice(dotIndex).toLowerCase() : '';
};

const getBaseName = (fileName: string) => {
  const normalized = fileName.replace(/\\/g, '/');
  return normalized.split('/').filter(Boolean).pop() || normalized;
};

const isAllowedImageName = (fileName: string) =>
  ALLOWED_IMAGE_EXTENSIONS.includes(getFileExtension(fileName));

const isMarkdownArchiveEntry = (fileName: string) =>
  ['.md', '.markdown', '.txt'].includes(getFileExtension(fileName));

const isIgnoredArchiveEntry = (fileName: string) => {
  const normalized = fileName.replace(/\\/g, '/').toLowerCase();
  return (
    normalized.startsWith('__macosx/') ||
    normalized.endsWith('/.ds_store') ||
    normalized.includes('/.ds_store')
  );
};

const scoreMarkdownEntry = (entryName: string) => {
  const normalized = entryName.replace(/\\/g, '/').toLowerCase();
  const baseName = getBaseName(normalized);
  let score = 0;

  if (getFileExtension(baseName) === '.md') {
    score += 4;
  }
  if (!normalized.includes('/')) {
    score += 4;
  }
  if (baseName.includes('answer') || baseName.includes('guide')) {
    score += 2;
  }
  if (entryName.includes('指南') || entryName.includes('回答')) {
    score += 2;
  }
  if (entryName.includes('长图') || normalized.includes('infographic')) {
    score -= 8;
  }
  if (normalized.includes('/assets/')) {
    score -= 8;
  }

  return score;
};

const chooseMarkdownEntry = (entries: JSZip.JSZipObject[]) => {
  return entries
    .filter((entry) => isMarkdownArchiveEntry(entry.name))
    .sort((left, right) => {
      const scoreDelta =
        scoreMarkdownEntry(right.name) - scoreMarkdownEntry(left.name);
      return scoreDelta || left.name.localeCompare(right.name);
    })[0];
};

const createArchiveImageFile = async (
  entry: JSZip.JSZipObject,
): Promise<ArchiveImageFile> => {
  const blob = await entry.async('blob');
  const extension = getFileExtension(entry.name);
  const file = new File([blob], getBaseName(entry.name), {
    type: IMAGE_MIME_TYPES[extension] || 'application/octet-stream',
  }) as ArchiveImageFile;
  file.archivePath = entry.name;
  return file;
};

const getDeclaredEntrySize = (entry: JSZip.JSZipObject) => {
  const entryWithData = entry as JSZip.JSZipObject & {
    _data?: { uncompressedSize?: number };
  };
  // JSZip exposes the declared uncompressed size only on this internal field.
  // eslint-disable-next-line no-underscore-dangle
  return entryWithData._data?.uncompressedSize || 0;
};

const getReadableError = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '';
};

const getUploadError = (fileName: string, error: unknown) => {
  const message = getReadableError(error);
  return `${fileName} 上传失败${
    message
      ? `：${message}`
      : '：请确认已登录，且图片是真实 JPG/PNG/GIF/WebP，未超过后台大小限制。'
  }`;
};

const AiAnswerImporter = ({ content, onImport }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const batchImageInputRef = useRef<HTMLInputElement>(null);
  const imageInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [visible, setVisible] = useState(false);
  const [rawContent, setRawContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [imageFiles, setImageFiles] = useState<Record<string, File>>({});
  const [mode, setMode] = useState<AnswerImportMode>('append');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<ImportNotice | null>(null);
  const [uploading, setUploading] = useState(false);
  const { verifyImageSize, uploadSingleFile } = useImageUpload();
  const stats = useMemo(() => getImportedAnswerStats(rawContent), [rawContent]);
  const imagePlaceholders = useMemo(
    () => extractAnswerImagePlaceholders(rawContent),
    [rawContent],
  );
  const matchedImageCount = imagePlaceholders.filter(
    (placeholder) => imageFiles[placeholder.id],
  ).length;

  const resetTransientState = () => {
    setError('');
    setNotice(null);
  };

  const openImporter = () => {
    setMode(content.trim() ? 'append' : 'replace');
    resetTransientState();
    setVisible(true);
  };

  const closeImporter = () => {
    if (uploading) {
      return;
    }
    setVisible(false);
    resetTransientState();
  };

  const applyRawContent = (
    value: string,
    name = '',
    availableImages: File[] = [],
  ) => {
    const placeholders = extractAnswerImagePlaceholders(value);
    const matchResult =
      placeholders.length > 0 && availableImages.length > 0
        ? matchAnswerImageFiles(placeholders, availableImages)
        : null;
    const matchedFiles = matchResult?.matchedFiles || {};
    const matchedFilesList = Object.values(matchedFiles);

    setRawContent(value);
    setFileName(name);
    setImageFiles(matchedFiles);
    setError('');

    if (placeholders.length === 0) {
      setNotice(
        availableImages.length > 0
          ? {
              text: `已读取 ${availableImages.length} 张图片，但 Markdown 没有本地图片占位，图片不会上传。`,
              variant: 'info',
            }
          : null,
      );
      return;
    }

    if (availableImages.length === 0) {
      setNotice(null);
      return;
    }

    if (matchedFilesList.length > 0 && !verifyImageSize(matchedFilesList)) {
      setImageFiles({});
      setNotice({
        text: 'Zip 中的图片已识别，但未通过站点图片格式或大小限制，请替换后重试。',
        variant: 'warning',
      });
      return;
    }

    const missingNames =
      matchResult?.missingPlaceholders.map((item) => item.id).join('、') || '';
    const ignoredCount = matchResult?.ignoredFiles.length || 0;
    setNotice({
      text: [
        `已自动匹配 ${matchedFilesList.length}/${placeholders.length} 张图片。`,
        missingNames ? `缺少：${missingNames}。` : '',
        ignoredCount > 0
          ? `已忽略未被 Markdown 引用的图片 ${ignoredCount} 张。`
          : '',
      ]
        .filter(Boolean)
        .join(' '),
      variant: missingNames ? 'warning' : 'success',
    });
  };

  const updateRawContent = (value: string, name = '') => {
    setRawContent(value);
    setFileName(name);
    setImageFiles({});
    resetTransientState();
  };

  const readClipboard = async () => {
    try {
      const value = await navigator.clipboard.readText();
      if (!value.trim()) {
        setError('剪贴板里没有可导入的文本。');
        return;
      }
      updateRawContent(value, '剪贴板');
    } catch {
      setError('浏览器未允许读取剪贴板，请在下方输入框中按 Ctrl+V 粘贴。');
    }
  };

  const selectFile = () => {
    fileInputRef.current?.click();
  };

  const selectBatchImages = () => {
    batchImageInputRef.current?.click();
  };

  const readZipFile = async (file: File) => {
    try {
      const zip = await JSZip.loadAsync(file);
      const entries = Object.values(zip.files).filter(
        (entry) => !entry.dir && !isIgnoredArchiveEntry(entry.name),
      );
      const markdownEntry = chooseMarkdownEntry(entries);
      if (!markdownEntry) {
        setError('Zip 中没有找到 .md、.markdown 或 .txt 文件。');
        return;
      }
      if (
        getDeclaredEntrySize(markdownEntry) > MAX_MARKDOWN_UNCOMPRESSED_SIZE
      ) {
        setError('Zip 中的 Markdown 解压后不能超过 2 MB。');
        return;
      }

      const imageEntries = entries.filter((entry) =>
        isAllowedImageName(entry.name),
      );
      if (imageEntries.length > MAX_ARCHIVE_IMAGE_COUNT) {
        setError(
          `Zip 中图片数量不能超过 ${MAX_ARCHIVE_IMAGE_COUNT} 张。请只保留 Markdown 实际引用的图片。`,
        );
        return;
      }
      const declaredImageSize = imageEntries.reduce(
        (total, entry) => total + getDeclaredEntrySize(entry),
        0,
      );
      if (declaredImageSize > MAX_ARCHIVE_TOTAL_IMAGE_SIZE) {
        setError('Zip 中图片声明解压总大小超过 25 MB，请压缩图片后重试。');
        return;
      }

      const archiveImages = await Promise.all(
        imageEntries.map(createArchiveImageFile),
      );
      const totalImageSize = archiveImages.reduce(
        (total, archiveImage) => total + archiveImage.size,
        0,
      );
      if (totalImageSize > MAX_ARCHIVE_TOTAL_IMAGE_SIZE) {
        setError('Zip 中图片解压后总大小超过 25 MB，请压缩图片后重试。');
        return;
      }

      const markdown = await markdownEntry.async('string');
      applyRawContent(
        markdown,
        `${file.name} / ${markdownEntry.name}`,
        archiveImages,
      );
    } catch (readError) {
      setError(
        `Zip 读取失败。请确认它是普通 zip 包，且只包含 Markdown 和图片：${getReadableError(
          readError,
        )}`,
      );
    }
  };

  const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const extension = getFileExtension(file.name);
    if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
      setError('仅支持 .md、.markdown、.txt、.json 和 .zip 文件。');
      return;
    }
    if (file.size > MAX_IMPORT_FILE_SIZE) {
      setError('导入文件不能超过 20 MB。');
      return;
    }

    if (extension === '.zip') {
      await readZipFile(file);
      return;
    }

    try {
      applyRawContent(await file.text(), file.name);
    } catch {
      setError('文件读取失败，请改用复制粘贴。');
    }
  };

  const readBatchImages = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      isAllowedImageName(file.name),
    );
    event.target.value = '';
    if (files.length === 0) {
      return;
    }
    if (imagePlaceholders.length === 0) {
      setError('当前 Markdown 中没有本地图片占位，暂时不需要选择图片。');
      return;
    }
    if (!verifyImageSize(files)) {
      return;
    }

    const matchResult = matchAnswerImageFiles(imagePlaceholders, files);
    const matchedFiles = Object.values(matchResult.matchedFiles);
    if (matchedFiles.length === 0) {
      setError(
        '没有找到与图片占位同名的文件，请检查 Markdown 路径和图片文件名。',
      );
      return;
    }

    setImageFiles((current) => ({
      ...current,
      ...matchResult.matchedFiles,
    }));
    setError('');
    setNotice({
      text: `批量选择已匹配 ${matchedFiles.length}/${imagePlaceholders.length} 张图片。`,
      variant:
        matchResult.missingPlaceholders.length > 0 ? 'warning' : 'success',
    });
  };

  const selectImage = (id: string) => {
    imageInputRefs.current[id]?.click();
  };

  const readImage = (id: string, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !verifyImageSize([file])) {
      return;
    }

    setImageFiles((current) => ({ ...current, [id]: file }));
    resetTransientState();
  };

  const importAnswer = async () => {
    const normalized = normalizeImportedAnswer(rawContent);
    if (!normalized) {
      setError('请先粘贴 AI 回答，或选择一个 Markdown/文本/Zip 文件。');
      return;
    }

    const missingImages = imagePlaceholders.filter(
      (placeholder) => !imageFiles[placeholder.id],
    );
    if (missingImages.length > 0) {
      setError(
        `还有 ${missingImages.length} 张图片没有匹配：${missingImages
          .map((item) => item.id)
          .join(
            '、',
          )}。可以选择包含这些文件的 Zip，或批量选择 assets 目录里的图片。`,
      );
      return;
    }

    setUploading(true);
    setError('');
    try {
      const uploadedEntries = await Promise.all(
        imagePlaceholders.map(async (placeholder) => {
          const imageFile = imageFiles[placeholder.id];
          try {
            const url = await uploadSingleFile(imageFile);
            if (!url) {
              throw new Error('');
            }
            return [placeholder.id, url] as const;
          } catch (uploadError) {
            throw new Error(getUploadError(imageFile.name, uploadError));
          }
        }),
      );
      const uploadedUrls = Object.fromEntries(uploadedEntries);
      const answerWithImages = replaceAnswerImagePlaceholders(
        normalized,
        uploadedUrls,
      );

      onImport(mergeImportedAnswer(content, answerWithImages, mode));
      setRawContent('');
      setFileName('');
      setImageFiles({});
      setVisible(false);
      resetTransientState();
    } catch (uploadError) {
      setError(
        `${getReadableError(uploadError) || '图片上传失败'} 回答内容尚未导入。`,
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button type="button" size="sm" variant="primary" onClick={openImporter}>
        <Icon type="bi" name="box-arrow-in-down" className="me-1" />
        导入 AI 回答
      </Button>

      <Modal
        visible={visible}
        title="导入 AI 回答"
        className="ai-answer-import-modal"
        bodyClass="ai-answer-import-body"
        scrollable
        confirmText={uploading ? '正在上传图片...' : '导入到回答框'}
        confirmBtnVariant="primary"
        confirmBtnDisabled={!rawContent.trim() || uploading}
        cancelBtnVariant="link"
        onCancel={closeImporter}
        onConfirm={importAnswer}>
        <p className="text-secondary small mb-3">
          支持直接粘贴 Markdown，也支持导入 AI 生成的 Zip 包。Zip 包只会读取
          Markdown 和被 Markdown 引用的 JPG/PNG/GIF/WebP
          图片，不会执行脚本或上传压缩包本体。
        </p>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={readClipboard}>
            <Icon type="bi" name="clipboard" className="me-1" />
            读取剪贴板
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={selectFile}>
            <Icon type="bi" name="file-earmark-arrow-up" className="me-1" />
            选择 Markdown/Zip
          </Button>
          {imagePlaceholders.length > 0 && (
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={selectBatchImages}>
              <Icon type="bi" name="images" className="me-1" />
              批量匹配图片
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="d-none"
            accept=".md,.markdown,.txt,.json,.zip,text/markdown,text/plain,application/json,application/zip,application/x-zip-compressed"
            onChange={readFile}
          />
          <input
            ref={batchImageInputRef}
            type="file"
            className="d-none"
            multiple
            accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
            onChange={readBatchImages}
          />
          {fileName && (
            <span className="ai-answer-import-source">
              <Icon type="bi" name="check-circle-fill" className="me-1" />
              {fileName}
            </span>
          )}
        </div>

        <Form.Group controlId="aiAnswerImportContent">
          <Form.Label className="fw-semibold">回答内容</Form.Label>
          <Form.Control
            as="textarea"
            rows={14}
            value={rawContent}
            className="font-monospace ai-answer-import-input"
            placeholder={GEO_AI_ANSWER_TEMPLATE}
            onChange={(event) => updateRawContent(event.target.value)}
          />
        </Form.Group>

        {rawContent.trim() && (
          <div className="ai-answer-import-stats">
            <span>{stats.characters} 字符</span>
            <span>{stats.headings} 个标题</span>
            <span>{stats.listItems} 个列表项</span>
            <span>{stats.links} 个链接</span>
            <span>{stats.codeBlocks} 个代码块</span>
            <span>
              {matchedImageCount}/{stats.imageSlots} 张本地图片已匹配
            </span>
          </div>
        )}

        {notice && (
          <Alert variant={notice.variant} className="mt-3 mb-0 py-2">
            {notice.text}
          </Alert>
        )}

        {imagePlaceholders.length > 0 && (
          <div className="ai-answer-image-slots">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-2">
              <Form.Label className="fw-semibold mb-0">
                回答中的图片位置
              </Form.Label>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                disabled={uploading}
                onClick={selectBatchImages}>
                <Icon type="bi" name="images" className="me-1" />
                按文件名批量匹配
              </Button>
            </div>
            {imagePlaceholders.map((placeholder, index) => (
              <div className="ai-answer-image-slot" key={placeholder.id}>
                <div className="ai-answer-image-slot-index">{index + 1}</div>
                <div className="ai-answer-image-slot-info">
                  <strong>{placeholder.alt}</strong>
                  <span>{placeholder.id}</span>
                </div>
                <input
                  ref={(element) => {
                    imageInputRefs.current[placeholder.id] = element;
                  }}
                  type="file"
                  className="d-none"
                  accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
                  onChange={(event) => readImage(placeholder.id, event)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant={
                    imageFiles[placeholder.id]
                      ? 'outline-success'
                      : 'outline-primary'
                  }
                  disabled={uploading}
                  onClick={() => selectImage(placeholder.id)}>
                  <Icon
                    type="bi"
                    name={imageFiles[placeholder.id] ? 'check-circle' : 'image'}
                    className="me-1"
                  />
                  {imageFiles[placeholder.id]?.name || '选择对应图片'}
                </Button>
              </div>
            ))}
            <div className="small text-secondary mt-2">
              Zip 导入时会按 Markdown 中的图片路径自动匹配，例如
              <code>assets/support_email_request.png</code>
              会匹配同名图片；未被 Markdown 引用的图片不会上传。
            </div>
          </div>
        )}

        <Form.Label className="fw-semibold mt-3 mb-2">导入方式</Form.Label>
        <ButtonGroup className="ai-answer-import-mode">
          <Button
            type="button"
            variant={mode === 'append' ? 'primary' : 'outline-secondary'}
            onClick={() => setMode('append')}>
            追加到当前回答
          </Button>
          <Button
            type="button"
            variant={mode === 'replace' ? 'primary' : 'outline-secondary'}
            onClick={() => setMode('replace')}>
            替换当前回答
          </Button>
        </ButtonGroup>

        {mode === 'replace' && content.trim() && (
          <Alert variant="warning" className="mt-3 mb-0 py-2">
            替换会覆盖回答框里的现有内容，但在正式发布前仍可撤销或修改。
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mt-3 mb-0 py-2">
            {error}
          </Alert>
        )}

        <div className="ai-answer-import-note">
          图片约定：公开图片使用
          <code>![说明](https://图片地址)</code>；Zip 包内图片使用
          <code>![说明](assets/image-name.png)</code>，或本地占位
          <code>![说明](answer-geo-image://image-name)</code>。不要使用
          Base64、HTML 脚本、SVG 或可执行附件。
        </div>
      </Modal>
    </>
  );
};

export default AiAnswerImporter;
